import {
  DocumentTextIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { createContext, useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Progress as DaisyProgress } from '@/components/ui/daisy/progress';

import {
  FileUploadItemActionsProps,
  FileUploadItemContentProps,
  FileUploadItemMetaProps,
  FileUploadItemNameProps,
  FileUploadItemProgressProps,
  FileUploadItemProps,
  FileUploadStatus,
} from './types';

const FileUploadItemContext = createContext<FileUploadStatus>('uploaded');

function ItemBase({
  status,
  className,
  children,
  ...props
}: FileUploadItemProps) {
  const isFailed = status === 'failed';

  return (
    <FileUploadItemContext.Provider value={status}>
      <div
        {...props}
        className={twMerge(
          'flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl border border-base-300 bg-base-100 p-4',
          isFailed && 'border-error',
          className,
        )}
      >
        <span className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-base-200">
          <DocumentTextIcon
            className={twMerge('size-5 text-primary', isFailed && 'text-error')}
          />
        </span>
        {children}
      </div>
    </FileUploadItemContext.Provider>
  );
}

function Content({ className, ...props }: FileUploadItemContentProps) {
  return (
    <div
      {...props}
      className={twMerge('flex min-w-0 flex-1 flex-col', className)}
    />
  );
}

function Name({ className, ...props }: FileUploadItemNameProps) {
  return (
    <p
      {...props}
      className={twMerge(
        'truncate font-semibold text-base-content text-sm leading-5',
        className,
      )}
    />
  );
}

function Meta({ className, children, ...props }: FileUploadItemMetaProps) {
  const status = useContext(FileUploadItemContext);
  const isFailed = status === 'failed';

  return (
    <p
      {...props}
      className={twMerge(
        'flex items-start gap-1 text-sm text-base-content/70',
        isFailed && 'text-error',
        className,
      )}
    >
      <ConditionalRenderer condition={isFailed}>
        <ExclamationCircleIcon className="mt-0.5 size-4 shrink-0" />
      </ConditionalRenderer>
      {children}
    </p>
  );
}

function Progress({ className, ...props }: FileUploadItemProgressProps) {
  return (
    <DaisyProgress
      {...props}
      color="primary"
      max={100}
      className={twMerge('mt-2 w-full', className)}
    />
  );
}

function Actions({ className, ...props }: FileUploadItemActionsProps) {
  return (
    <div
      {...props}
      className={twMerge(
        'flex shrink-0 flex-wrap items-center justify-end gap-2',
        'max-sm:basis-full max-sm:justify-start max-sm:pl-16',
        className,
      )}
    />
  );
}

ItemBase.displayName = 'FileUpload.Item';
Content.displayName = 'FileUpload.Item.Content';
Name.displayName = 'FileUpload.Item.Name';
Meta.displayName = 'FileUpload.Item.Meta';
Progress.displayName = 'FileUpload.Item.Progress';
Actions.displayName = 'FileUpload.Item.Actions';

const FileUploadItem = Object.assign(ItemBase, {
  Content,
  Name,
  Meta,
  Progress,
  Actions,
});

export default FileUploadItem;
